import type { ApolloClient } from '@apollo/client';
import type { ApolloLink } from '@apollo/client/link';
import { VALID_USER_TOKEN } from '@quesmed/types/resolvers/mutation/validUserToken';
import { mergeMap, Observable } from 'rxjs';
import { ROUTES } from '@/config/routes';
import { toaster } from '@/core/ui/Overlay/Toast/Toast';
import { ClientStorage } from '@/utils';
import { client } from '../client';

const promiseToObservable = (promise: Promise<unknown>) => {
  return new Observable((subscriber) => {
    promise.then(
      (value) => {
        if (subscriber.closed) {
          return;
        }
        subscriber.next(value);
        subscriber.complete();
      },
      (err) => {
        subscriber.error(err);
      },
    );
  });
};

const renewToken = async (client: ApolloClient) => {
  try {
    const { data, error } = await client.mutate<{ validUserToken: { renewToken: string } }>({
      mutation: VALID_USER_TOKEN,
    });
    if (!data?.validUserToken?.renewToken || error) {
      throw new Error('Token refresh failed due to potential tampering. Please log in again');
    }
    const newToken = data.validUserToken.renewToken;
    ClientStorage.cookies.setItem('ques-usertoken', newToken, {
      expires: 7 * 24 * 60 * 60,
    });

    return newToken;
  } catch (error) {
    toaster.error((error as Error)?.message);
    throw error;
  }
};

export const handleRefreshToken = (operation: ApolloLink.Operation, forward: ApolloLink.ForwardFunction) => {
  const hasChecked = ClientStorage.cookies.getItem('token-check');
  if (hasChecked) {
    // added cookie to check to avoid infinite loop for renewing token
    // because renewToken throw same error that had trigger renew flow
    setTimeout(() => {
      ClientStorage.cookies.removeItem('token-check');
      window.location.href = ROUTES.LOGIN;
    }, 2000);
    return;
  }
  ClientStorage.cookies.setItem('token-check', true);

  return promiseToObservable(renewToken(client)).pipe(
    mergeMap((newToken) => {
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          authorization: `Bearer ${newToken}`,
        },
      }));
      // Retry the failed GraphQL API request internally after renewing the token,
      // ensuring the user is unaware of the retry process.
      return forward(operation);
    }),
  );
};
